// app/ has no slice, so no lib/ to hold pure functions — colocate the handler next
// to the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { BIOTRICITY_SUPPORT_ADDRESS } from '@/shared/config';
import { t } from 'ttag';

// *: \u2011 is non-breaking hyphen unicode

/**
 * Builds the past-due account warning copy for the 30, 40, 50 and 60 day escalation stages. Each stage pairs a title with a message describing the tightening restrictions - losing non-urgent reports, then instant report generation, then starting new studies - and embeds the billing email and phone taken from BIOTRICITY_SUPPORT_ADDRESS.
 *
 * @returns Title and message pairs keyed by DAY_30, DAY_40, DAY_50 and DAY_60.
 */
const getEscalatedMessage = () => {
  const { billingMail, phone } = BIOTRICITY_SUPPORT_ADDRESS || {};
  const message = {
    DAY_30: {
      title: t`ATTENTION REQUIRED`,
      message: (
        <span>
          {t`Your account is greater than 30 days past due. We want to work with you to bring the account current. Please notify your accounts payable team that they need to make payment and provide payment advice immediately or contact us to make payment arrangements to maintain your account in good standing. We can be reached at`}{' '}
          <a className="colored-text mail-to" href={`mailto:${billingMail}`}>
            {billingMail}
          </a>{' '}
          {t`or by calling`} <span className="inline-flex">{`1-${phone}`}</span>
        </span>
      ),
    },
    DAY_40: {
      title: t`URGENT ATTENTION REQUIRED`,
      message: (
        <span>
          {t`Your account is greater than 40 days past due. We want to work with you to maintain your account in good standing. Your account will be restricted from receiving reports, other than urgent and emergent notification reports, as well as generating and re-generating instant reports within 10 days. Please notify your accounts payable team that they need to make payment and provide payment advice immediately or contact us to make payment arrangements. We can be reached at`}{' '}
          <a className="colored-text mail-to" href={`mailto:${billingMail}`}>
            {billingMail}
          </a>{' '}
          {t`or by calling`} <span className="inline-flex">{`1-${phone}`}</span>
        </span>
      ),
    },
    DAY_50: {
      title: t`URGENT ATTENTION REQUIRED`,
      message: (
        <span>
          {t`Your account is greater than 50 days past due. We want to work with you to maintain your account in good standing. Your account has been restricted from receiving reports other than urgent and emergent notification reports, as well as generating and re-generating instant reports and you will be restricted from starting new studies within 10 days. Please notify your accounts payable team that they need to make payment and provide payment advice immediately or contact us to make payment arrangements. We can be reached at`}{' '}
          <a className="colored-text mail-to" href={`mailto:${billingMail}`}>
            {billingMail}
          </a>{' '}
          {t`or by calling`} <span className="inline-flex">{`1-${phone}`}</span>
        </span>
      ),
    },
    DAY_60: {
      title: t`URGENT ATTENTION REQUIRED`,
      message: (
        <span>
          {t`Your account is greater than 60 days past due and has been restricted from starting new studies as well as generating and re-generating instant reports. Urgent and emergent notification reports on previously started studies will continue to be provided. We want to work with you to restore your account in good standing. Please notify your accounts payable team that they need to make payment and provide payment advice immediately or contact us to make payment arrangements. We can be reached at`}{' '}
          <a className="colored-text mail-to" href={`mailto:${billingMail}`}>
            {billingMail}
          </a>{' '}
          {t`or by calling`} <span className="inline-flex">{`1-${phone}`}</span>
        </span>
      ),
    },
  };
  return message;
};

export { getEscalatedMessage };
